<template>
  <div>
    <NuxtLoadingIndicator color="var(--primary)" />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <Toaster position="top-center" />
    <audio
      ref="newMessageNotification"
      src="/chat/new-message.mp3"
    />
  </div>
</template>

<script setup lang="ts">
import 'vue-sonner/style.css';
import { Toaster } from '~/components/ui/sonner';
import { toast } from 'vue-sonner';
import { useClient } from "~/stores/client";
import { Centrifuge } from "centrifuge";
import type { Notification } from "~/entities/notification";
import { useNotifications } from "~/stores/notifications";
import {initializeFaro, EVENT_ROUTE_CHANGE, getWebInstrumentations} from '@grafana/faro-web-sdk';

const runtimeConfig = useRuntimeConfig();
const router = useRouter();
const client = useClient();
const { callHook } = useNuxtApp();

//-----VARIABLES-----\\
let pingTimer: Timeout|null = null;

//-----STATE-----\\
const centrifuge = ref<Centrifuge|null>(null);

//Уведомления
const notificationsStore = useNotifications();
const newMessageNotification = ref();

//-----COMPUTED-----\\
watch(() => client.isAuthenticated, (isAuthenticated: boolean) => {
  if (isAuthenticated) {
    auth();
  } else {
    clearTimeout(pingTimer);
    centrifugeDisconnect();
    // Reset between users so the next login cannot briefly show stale data.
    notificationsStore.resetAll();
  }
});

//-----METHODS-----\\
function auth() {
  loadNotifications();
  if (runtimeConfig.public.realtimeEnabled) {
    centrifugeConnect();
  }
  ping();
}
async function centrifugeConnect() {
  if (!client.isAuthenticated || centrifuge.value !== null) {
    return;
  }
  const { data, error } = await mainFetch('chats/auth', { method: 'POST' })
  if (error.value !== null || data.value?.token === undefined) {
    return
  }
  centrifuge.value = new Centrifuge(realtimeUrl(), { token: data.value.token });
  unref(centrifuge).connect();
  subscribeNotificationChannel();
  webSocketChat();
}
/** `NUXT_PUBLIC_REALTIME_URL` (dev: the worktree Centrifugo), else `chat.<service domain>`. */
function realtimeUrl(): string {
  return runtimeConfig.public.realtimeUrl
    || `${runtimeConfig.public.serviceProtocol === 'https' ? 'wss' : 'ws'}://chat.${runtimeConfig.public.serviceDomain}/connection/websocket`;
}
function centrifugeDisconnect() {
  if (centrifuge.value === null) {
    return;
  }
  unref(centrifuge).disconnect();
  centrifuge.value = null;
}
async function loadNotifications() {
  if (!client.getClientId) {
    return;
  }

  await notificationsStore.loadPreview();
}
function subscribeNotificationChannel() {
  if (centrifuge.value === null) {
    return;
  }

  unref(centrifuge).on('publication', ctx => {
    if (ctx.channel !== 'notifications:#' + client.getClientId) {
      return;
    }

    if (ctx.data.event === 'new') {
      const incoming = {
        ...ctx.data.notification,
        created: new Date(ctx.data.notification.created * 1000),
      } as Notification;
      notificationsStore.pushIncoming(incoming);

      toast(ctx.data.notification.title);
    } else if (ctx.data.event === 'channelConnected') {
      callHook('channel-connected', ctx.data.channel);
    }
  });
}
function playChatNotification() {
  newMessageNotification.value.currentTime = 0;
  newMessageNotification.value.play();
}
function webSocketChat() {
  unref(centrifuge).on('publication', ctx => {
    if (ctx.channel !== 'chats:#' + client.getClientId) {
      return;
    }

    if (ctx.data.event === 'newMessage') {
      playChatNotification();
    }

    callHook('chat', ctx.data);
  });
}
function ping() {
  if (!client.isAuthenticated) {
    return;
  }

  mainFetch('clients/ping').then(() => {
    pingTimer = setTimeout(ping, 120000);
  });
}
function connectFaro() {
  if (import.meta.dev || !runtimeConfig.public.analyticsEnabled) {
    return;
  }

  const faro = initializeFaro({
    url: 'https://rum.sobes.ru/collect',
    apiKey: '50158727-bc58-47f1-a427-a9564c573879',
    app: {
      name: 'sobes-frontend',
      version: '1.0.1',
    },
    instrumentations: [
      ...getWebInstrumentations()
    ]
  });

  router.afterEach((to, from) => {
    faro.api.pushEvent(EVENT_ROUTE_CHANGE, {
      toRoute: to.path,
      toUrl: to.fullPath,
      fromRoute: from.path,
      fromUrl: from.fullPath,
    });
  });
}

onMounted(async () => {
  await nextTick();
  if (client.isAuthenticated) {
    auth();
  }
  connectFaro();
});
onUnmounted(() => {
  centrifugeDisconnect();
});
</script>
